import { Cell, Column, HeaderCell } from 'rsuite-table';
import React, { RefObject } from 'react';
import { TFunction } from 'i18next';
import {
    CellIconType,
    ColumnType,
    FilterValue,
    getColumnLabel,
    GridDataRow,
    PopupMode,
    PopupType,
    TextAlign,
} from 'components/common/grid/';
import { Box, Tooltip, Typography } from '@mui/material';
import { PopupSettings } from 'components/common/popup/Popup';
import IconHandler from 'components/common/icon/IconHandler';
import { Stack, SxProps } from '@mui/system';
import { Theme } from '@mui/material/styles';
import { SortType } from 'rsuite-table/lib/types';
import IconMi from 'components/common/icon/IconMi';
import LightTooltip from 'components/common/tooltip/LightTooltip';
import { FormRendererAPIType } from 'components/common/form';
import parse from 'html-react-parser';
import { Components } from './ComponentsRegistry';
import striptags from 'striptags';

const buildColumn = (
    column: ColumnType,
    index: number,
    t: TFunction,
    gridName: string,
    dataKey: string,
    width?: number,
    reloadGridData?: () => void,
    onResize?: (columnWidth?: number, dataKey?: string) => void,
    setPopupUrl?: (url: string) => void,
    setPopupConfig?: (popupConfig: PopupSettings) => void,
    setPopupType?: (type: PopupType) => void,
    setPopupMode?: (type: PopupMode) => void,
    customCells?: { [key: string]: any },
    setShowLoader?: (value: boolean) => void,
    order?: SortType,
    orderBy?: string,
    setPopupComponent?: (popupComponent: string) => void,
    setPopupComponentProps?: (componentProps: { [key: string]: string }) => void,
    setErrorMessage?: (message: string) => void,
    form?: FormRendererAPIType,
    setGridDataChanged?: (state: boolean) => void,
    filtersValue?: FilterValue,
    headerRef?: RefObject<HTMLDivElement>,
    gridEntityType?: string,
) => {
    const sortable = column.sortable ?? false;

    const widthParams = width
        ? { width: width, resizable: column.resizable ?? true }
        : { flexGrow: 100, resizable: column.resizable ?? true };

    const tooltip = column.tooltip && column.tooltip > '' ? <Typography>{t(column.tooltip) as string}</Typography> : '';

    const columnFixed = column.fixed ?? false;

    return (
        <Column
            {...widthParams}
            minWidth={40}
            sortable={sortable}
            key={index}
            align="left"
            onResize={onResize}
            fixed={columnFixed}
        >
            <HeaderCell
                ref={headerRef}
                align={'left'}
                data-test={`grid_${gridName}_${column.name}_column_header`}
                renderSortIcon={(sortType) => {
                    return (
                        <IconMi
                            icon={'arrow-right'}
                            fontSize={'16'}
                            sx={{ transform: sortType === 'desc' ? 'rotate(90deg)' : 'rotate(-90deg)' }}
                        />
                    );
                }}
                className={`${orderBy === column.name ? 'sortable-active' : ''}`}
            >
                <Tooltip title={tooltip} placement="bottom">
                    <Typography component={'span'} className={'rs-table-cell-header-title'}>
                        {getColumnLabel(column, t)}
                    </Typography>
                </Tooltip>
            </HeaderCell>
            {getCellComponent(
                column,
                t,
                gridName,
                dataKey,
                reloadGridData,
                setPopupUrl,
                setPopupConfig,
                setPopupType,
                setPopupMode,
                customCells,
                setShowLoader,
                setPopupComponent,
                setPopupComponentProps,
                setErrorMessage,
                form,
                setGridDataChanged,
                filtersValue,
                headerRef,
                columnFixed,
                gridEntityType,
            )}
        </Column>
    );
};

const getComponent = (componentName: string, customCells?: { [key: string]: any }) => {
    if (customCells && customCells.hasOwnProperty(componentName)) {
        return customCells[componentName];
    }
    if (Components.hasOwnProperty(componentName)) {
        return Components[componentName];
    }

    return null;
};

const getCellComponent = (
    column: ColumnType,
    t: TFunction,
    gridName: string,
    dataKey: string,
    reloadGridData?: () => void,
    setPopupUrl?: (url: string) => void,
    setPopupConfig?: (popupConfig: PopupSettings) => void,
    setPopupType?: (type: PopupType) => void,
    setPopupMode?: (mode: PopupMode) => void,
    customCells?: { [key: string]: any },
    setShowLoader?: (value: boolean) => void,
    setPopupComponent?: (popupComponent: string) => void,
    setPopupComponentProps?: (componentProps: { [key: string]: string }) => void,
    setErrorMessage?: (message: string) => void,
    form?: FormRendererAPIType,
    setGridDataChanged?: (state: boolean) => void,
    filtersValue?: FilterValue,
    headerRef?: React.RefObject<HTMLDivElement>,
    columnFixed?: string | boolean,
    gridEntityType?: string,
) => {
    const drawCell = (rowData?: GridDataRow, rowIndex?: number) => {
        if (rowData) {
            const columnData = rowData[column.name];
            let processedCellComponent = null;

            if (columnData === undefined || columnData === null) {
                return;
            }

            if (typeof columnData === 'object') {
                const cellComponent = getComponent(columnData.component, customCells);

                if (cellComponent) {
                    processedCellComponent = React.createElement(cellComponent, {
                        ...columnData.config,
                        data: rowData,
                        reloadGridData,
                        setPopupUrl,
                        setPopupConfig,
                        setPopupType,
                        setPopupMode,
                        t,
                        setShowLoader,
                        setPopupComponent,
                        setPopupComponentProps,
                        setErrorMessage,
                        form,
                        setGridDataChanged,
                        dataTestCell: `grid_${gridName}_${column.name}_column_${rowData[dataKey]}_cell_action`,
                        filtersValue,
                        headerRef,
                        gridEntityType,
                    });
                } else {
                    processedCellComponent = (
                        <Typography noWrap align={'left'}>
                            {columnData.component}
                        </Typography>
                    );
                }
            } else {
                processedCellComponent = (
                    <Typography noWrap align={'left'}>
                        {String(columnData)}
                    </Typography>
                );
            }

            return (
                <Box
                    data-test={`grid_${gridName}_${column.name}_column_${rowData[dataKey]}_cell`}
                    sx={{
                        height: 1,
                        textAlign: columnFixed ? 'center' : '',
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: columnFixed ? 'center' : 'flex-start',
                    }}
                >
                    {processedCellComponent}
                </Box>
            );
        }
    };

    return (
        <Cell
            style={{ display: 'flex', justifyContent: columnFixed ? 'center' : 'flex-start' }}
            align={columnFixed ? 'center' : 'left'}
            dataKey={column.name}
            children={drawCell}
        />
    );
};

export const getIcon = (icon?: CellIconType, sx?: SxProps<Theme>) => {
    if (icon) {
        const iconEl = (
            <Box sx={{ display: 'inline-flex' }}>
                <IconHandler
                    icon={{ type: icon.type, value: icon.value }}
                    className={icon.class ?? undefined}
                    sx={{
                        ...{},
                        fontSize: icon.size ? `${icon.size}px` : undefined,
                        ...(sx && !icon.useWrapper ? sx : {}),
                    }}
                    color={icon.color}
                />
            </Box>
        );
        if (icon.useWrapper) {
            return (
                <Box sx={{ ...{}, ...(sx ?? {}) }} className={'icon-cell-holder'}>
                    {iconEl}
                </Box>
            );
        } else {
            return iconEl;
        }
    }

    return null;
};

export const getCellContent = (
    text?: string,
    icon?: CellIconType,
    rawHtml?: boolean,
    align?: TextAlign,
    subText?: string,
    color?: string,
    multilines?: number,
    stripTags?: boolean,
) => {
    if (rawHtml) {
        const sxConfig: any = {
            color: color,
            whiteSpace: 'normal',
            display: 'inline-flex',
        };

        if (!multilines) {
            sxConfig['br'] = { display: 'none' };
            sxConfig['*'] = { display: 'inline' };
            sxConfig['whiteSpace'] = 'nowrap';
        }

        return (
            <Stack>
                <Typography noWrap align={align} sx={sxConfig}>
                    {stripTags ? striptags(String(text), ['b', 'i', 'br']) : parse(text ? String(text) : '')}
                </Typography>
                {subText && <Typography>{subText}</Typography>}
            </Stack>
        );
    }

    return (
        <Stack>
            <Typography noWrap align={align} sx={{ display: 'inline-flex', color: color }}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center' }} component={'span'}>
                    {getIcon(icon, text ? { mr: 0.5 } : undefined)}
                    {text}
                </Box>
            </Typography>
            {subText && <Typography>{subText}</Typography>}
        </Stack>
    );
};

export const getCellWithTooltip = (
    content: React.JSX.Element,
    tooltip: string = '',
    rawTooltipHtml: boolean = false,
    t?: TFunction,
) => {
    if (rawTooltipHtml) {
        return (
            <LightTooltip
                followCursor
                placement={'top-start'}
                title={<span dangerouslySetInnerHTML={{ __html: tooltip }} />}
            >
                {content}
            </LightTooltip>
        );
    }

    return (
        <Tooltip followCursor placement={'top-start'} title={t ? t(tooltip) : (tooltip as string)}>
            {content}
        </Tooltip>
    );
};

export default buildColumn;
